import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { useTheme } from "@mui/joy/styles";
import categories from "../../data/categories.json";
import {
  fetchAdminMedia,
  AdminMediaItem,
  createAdminMedia,
  deleteAdminMedia,
  setAdminMediaFeatured,
} from "../../api";

export default function AdminMedia() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [media, setMedia] = useState<AdminMediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isFeaturing, setIsFeaturing] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchAdminMedia()
      .then((data) => {
        if (mounted) setMedia(data ?? []);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const resetForm = () => {
    setTitle("");
    setType("");
  };

  const handleUpload = async () => {
    if (!title.trim() || !type) return;
    setIsUploading(true);
    try {
      const newItem = await createAdminMedia({ title: title.trim(), type });
      if (newItem) {
        setMedia((prev) => [...prev, newItem as AdminMediaItem]);
      }
      resetForm();
    } finally {
      setIsUploading(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleFeature = async () => {
    if (selected.length === 0) return;
    setIsFeaturing(true);
    try {
      await setAdminMediaFeatured(selected);
      setSelected([]);
    } finally {
      setIsFeaturing(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteAdminMedia(id);
      setMedia((prev) => prev.filter((m) => m.id !== id));
      setSelected((prev) => prev.filter((i) => i !== id));
    } finally {
      setDeletingId((current) => (current === id ? null : current));
    }
  };

  const disableUpload = !title.trim() || !type || isUploading;
  const disableFeature = selected.length === 0 || isFeaturing;
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const uploadBackground = isDark
    ? "linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)"
    : "linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 255, 0.96) 100%)";
  const uploadOverlay = isDark
    ? "radial-gradient(75% 85% at 12% 12%, rgba(56, 189, 248, 0.16) 0%, transparent 65%), radial-gradient(70% 70% at 88% 85%, rgba(251, 191, 36, 0.14) 0%, transparent 70%)"
    : "radial-gradient(75% 85% at 12% 12%, rgba(255,214,153,0.22) 0%, transparent 65%), radial-gradient(70% 70% at 88% 85%, rgba(175,205,255,0.22) 0%, transparent 70%)";
  const emptyBackground = isDark
    ? "linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.96))"
    : "linear-gradient(135deg, rgba(238, 241, 250, 0.9), rgba(246, 248, 252, 0.95))";

  if (loading) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: "xl",
          p: 6,
          display: "grid",
          placeItems: "center",
          color: "text.secondary",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size="md" />
          <Typography level="body-md">Gathering your sacred gallery…</Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: "xl",
          p: { xs: 3, md: 4 },
          position: "relative",
          overflow: "hidden",
          borderColor: "divider",
          boxShadow: "sm",
          background: uploadBackground,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: uploadOverlay,
            pointerEvents: "none",
          }}
        />
        <Stack spacing={2.5} sx={{ position: "relative", zIndex: 1 }}>
          <Stack spacing={0.75}>
            <Typography level="title-sm" sx={{ fontWeight: 600, opacity: 0.85 }}>
              New darshan upload
            </Typography>
            <Typography level="body-md" sx={{ color: "text.secondary", maxWidth: 520 }}>
              Share today's transcendental moments. Title your media, choose a category, and let devotees relish the experience.
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "flex-end" }}
          >
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Highlight title"
              aria-label="media-title"
              sx={{
                flex: 1,
                minWidth: { md: 240 },
                backgroundColor: "background.surface",
              }}
            />
            <Select
              value={type}
              onChange={(_, value) => setType(value ?? "")}
              placeholder="Select seva category"
              aria-label="media-type"
              sx={{
                flexBasis: { md: 220 },
                backgroundColor: "background.surface",
              }}
            >
              {categories.mediaTypes.map((mediaType) => (
                <Option key={mediaType} value={mediaType}>
                  {mediaType}
                </Option>
              ))}
            </Select>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Clear fields">
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="md"
                  onClick={resetForm}
                  aria-label="reset-upload-form"
                  sx={{ alignSelf: "center" }}
                >
                  <RestartAltRoundedIcon />
                </IconButton>
              </Tooltip>
              <Button
                size="md"
                variant="soft"
                color="primary"
                startDecorator={<CloudUploadRoundedIcon />}
                onClick={handleUpload}
                loading={isUploading}
                disabled={disableUpload}
                data-testid="upload-media"
              >
                Upload seva moment
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3.5 }, gap: 2.5, boxShadow: "sm" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={0.5}>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Library overview
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              {media.length === 0
                ? "Your first upload will inspire countless devotees."
                : `Serving ${media.length} cherished media items.`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<StarRoundedIcon />}
              onClick={handleFeature}
              disabled={disableFeature}
              loading={isFeaturing}
              data-testid="feature-selected"
            >
              Feature selected ({selected.length || 0})
            </Button>
            {selected.length > 0 ? (
              <Button variant="plain" color="neutral" onClick={() => setSelected([])}>
                Clear selection
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {media.length === 0 ? (
            <Card
              variant="soft"
              color="neutral"
              sx={{
                borderRadius: "lg",
                p: 4,
                textAlign: "center",
                gap: 1.5,
                border: "1px solid",
                borderColor: "divider",
                background: emptyBackground,
              }}
            >
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Your gallery awaits its first seva highlight
            </Typography>
            <Typography level="body-sm">
              Offer darshan, kirtans, and community love. Every upload spreads Krishna consciousness farther.
            </Typography>
          </Card>
        ) : (
          <Stack spacing={1.5}>
            {media.map((item) => {
              const isSelected = selectedSet.has(item.id);
              return (
                <Card
                  key={item.id}
                  variant={isSelected ? "soft" : "outlined"}
                  color={isSelected ? "primary" : "neutral"}
                  sx={{
                    borderRadius: "lg",
                    p: 2,
                    transition: "transform 0.18s ease, box-shadow 0.18s ease",
                    boxShadow: isSelected ? "var(--joy-shadow-md)" : undefined,
                  }}
                >
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleSelect(item.id)}
                        aria-label={`select-${item.id}`}
                        slotProps={{ input: { "data-testid": `select-${item.id}` } }}
                      />
                      <Stack spacing={0.35} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm" noWrap sx={{ fontWeight: 600 }}>
                          {item.title}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="sm" variant="soft" color={isSelected ? "primary" : "neutral"}>
                            {item.type}
                          </Chip>
                          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                            ID: {item.id}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Remove from library">
                        <Button
                          variant="outlined"
                          color="danger"
                          startDecorator={<DeleteOutlineRoundedIcon />}
                          onClick={() => handleDelete(item.id)}
                          data-testid={`delete-${item.id}`}
                          loading={deletingId === item.id}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
